import { Field, Form, Formik } from "formik";
import { isEmpty } from "lodash";
import Button from "pages/_components/Button";
import GridTable from "pages/_components/GridTable/GridTable";
import Notification from "pages/_components/Notification";
import PageLoading from "pages/_components/PageLoading";
import TextField from "pages/_components/fields/TextField";
import AmountField from "pages/_components/fields/formik/AmountField";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import Selector from "pages/_components/fields/formik/Selector";
import { bool } from "prop-types";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { actions as transferActions } from "reducers/transfers";
import * as i18n from "util/i18n";
import { transferTypes } from "util/transfer";
import I18n from "pages/_components/I18n";
import MainContainer from "pages/_components/MainContainer";
import ResponsiveHeader from "pages/_components/header";
import { push } from "connected-react-router";
import useSchema from "hooks/useSchema";
import * as Yup from "yup";
import { actions as notificationActions } from "reducers/notification";
import { useAccount } from "hooks/useAccounts";
import * as configUtils from "util/config";
import useDebounce from "../hooks/useDebounce";
import { useOtherFieldChange } from "../hooks/useOtherFieldChange";
import { useOtherValidations } from "../hooks/useOtherValidations";
import { useTransferAccounts } from "../hooks/useTransferAccounts";
import Beneficiaries from "../Others/components/Beneficiaries";
import MepDisclaimer from "./components/MepDisclaimer";

const FORM_ID = "mep";

const MepTransfersPage = ({ isDesktop }) => {
    const dispatch = useDispatch();
    const { stringRequired, numberRequired, stringNullableRequired } = useSchema();
    const { debitAccountMep, fetching, accounts } = useTransferAccounts("mepTransfers");
    const { formRef, onChangeDebounce, setText } = useDebounce();
    const transfers = useSelector((state) => state.transfers);
    const transferData = transfers.otherTransferData;
    const { allowedMep } = transfers;
    const transferKindOptions = i18n.formatOption(transferTypes, "transfer.types");
    const mepReasons = configUtils.getArray("mep.reasons", []);
    const conceptOptions = i18n.formatOption(mepReasons, "mep.reasons");

    const { state } = useLocation();
    const placeholder = i18n.get("global.select");
    const { validateMepAccounts } = useOtherValidations(accounts);
    const debitAccountOptionsFiltered = debitAccountMep.filter((obj) => !obj.label.includes("CA U$D"));

    const { handleDebitAccount, handleAccountType, handleBeneficiary, handleConcept } = useOtherFieldChange(
        accounts,
        transferData,
        state,
    );

    const currencies = [
        { id: "ARS", label: i18n.get(`currency.label.ARS`) },
        { id: "USD", label: i18n.get(`currency.label.USD`) },
    ];

    const [prefixValue, setPrefixValue] = useState();

    useEffect(() => {
        dispatch(transferActions.generateMepValidation());
    }, []);

    const initialValues = {
        // eslint-disable-next-line no-nested-ternary
        debitAccount: transferData?.debitAccount,
        creditCbu: "",
        transferKind: "",
        amountField: {
            amount: null,
            // eslint-disable-next-line no-nested-ternary
            currency: transferData?.amountField?.currency || "ARS",
        },
        creditCuit: "",
        nameRecipient: "",
        beneficiary: {
            productType: "",
            localBank: "",
            accountNumber: "",
            recipientName: "",
            recipientDocumentType: "",
            recipientDocumentNumber: "",
            recipientEmail: "",
            cbu: "",
        },
        frequentDestination: false,
    };

    const handleOnChangeAmount = (amount, values) => {
        const { debitAccount = "" } = values;
        const selected = accounts.find((_account) => _account.productId === debitAccount);

        if (selected && values.amountField.amount > selected.availableBalance) {
            dispatch(
                notificationActions.showNotification(i18n.get("generate.mep.amount.validation"), "warning", [
                    "mepNotification",
                ]),
            );
        }
        dispatch(
            transferActions.setOtherTransferData({
                ...values,
                amountField: {
                    ...amount.values.amountField,
                },
            }),
        );
    };

    const mepTransferSchema = Yup.object().shape({
        debitAccount: stringRequired,
        creditCbu: stringRequired,
        reasonCode: stringNullableRequired,
        amountField: Yup.object().shape({
            amount: numberRequired,
        }),
    });

    const { selectedAccount: _debitAccount, formatAccountBody } = useAccount("productId", transferData.debitAccount);

    const handleSubmit = (values, formikBag) => {
        const isValidAccounts = validateMepAccounts(values.transferKind, _debitAccount.currency, values.beneficiary);
        const otherTransferData = {
            ...values,
            amount: {
                quantity: values.amountField.amount,
                currency: values.amountField.currency,
            },
            debitAccountLabel: formatAccountBody(_debitAccount),
            ownerName: _debitAccount?.ownerName,
            beneficiaryAccountLabel: values.beneficiary
                ? formatAccountBody({
                      accountNumber: values.beneficiary?.accountNumber,
                      accountType: values.beneficiary?.productType.split(" ")[0],
                      currency: values.beneficiary?.productType.split(" ")[1],
                  })
                : "",
            ownerCuit: _debitAccount?.taxBankCode,
            ownerCbu: _debitAccount?.uniformBankCode,
        };

        if (isValidAccounts) {
            dispatch(transferActions.setOtherTransferData(otherTransferData));

            dispatch(
                push({
                    pathname: `/mep/confirm`,
                    state,
                }),
            );
        }
        formikBag.setSubmitting(false);
    };

    const handleClick = () => {
        dispatch(push("/transfers"));
    };

    return (
        <>
            <ResponsiveHeader id="mep.title" className="header-without-button" backLinkTo="/desktop" />
            <MainContainer>
                <Notification scopeToShow="mepNotification" />
                <PageLoading loading={fetching}>
                    <Formik
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        validationSchema={mepTransferSchema}
                        ref={formRef}>
                        {({ values, setFieldValue, isSubmitting }) => {
                            const validAccount = debitAccountMep.find((account) => account.id === values.debitAccount);
                            const disableSubmit =
                                !values.debitAccount ||
                                !values.transferKind ||
                                !values.creditCbu ||
                                !values.amountField.amount ||
                                !values.reasonCode ||
                                !values.beneficiary.cbu;
                            isEmpty(validAccount);
                            setPrefixValue(
                                validAccount?.balance?.currency === "ARS"
                                    ? i18n.get(`currency.label.ARS`)
                                    : i18n.get(`currency.label.USD`),
                            );
                            return (
                                <Form>
                                    <GridTable className="form transfer-others">
                                        <GridTable.Container columns={12} columnsTemplate="fixed">
                                            <GridTable.Data className="required" columnStart={2} columnWidth={10}>
                                                <Field
                                                    component={ProductSelector}
                                                    label={i18n.get(`${FORM_ID}.debitAccount.label`)}
                                                    name="debitAccount"
                                                    placeholder={i18n.get(`${FORM_ID}.debitAccount.placeholder`)}
                                                    data={{
                                                        emptyOptionLabel: "",
                                                        options: debitAccountOptionsFiltered,
                                                    }}
                                                    onChange={(id) => handleDebitAccount(id, setFieldValue, values)}
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data columnStart={2} columnWidth={10}>
                                                <I18n component="h2" id={`${FORM_ID}.creditAccount.label`} />
                                            </GridTable.Data>
                                            <GridTable.Data className="required" columnStart={2} columnWidth={3}>
                                                <Field
                                                    component={Selector}
                                                    label={i18n.get(`${FORM_ID}.holder.label`)}
                                                    name="transferKind"
                                                    disabled={!values.debitAccount || values.beneficiary.cbu}
                                                    options={transferKindOptions.reverse()}
                                                    placeholder={placeholder}
                                                    onChange={(type) => handleAccountType(type, values)}
                                                />
                                            </GridTable.Data>
                                            <div
                                                className="required grid-table-data"
                                                style={{
                                                    gridColumn: "5 / span 4",
                                                    flexDirection: "row",
                                                    alignItems: "flex-end",
                                                    height: "100%",
                                                }}>
                                                <Field
                                                    component={TextField}
                                                    labelnoI18n={i18n.get(`${FORM_ID}.creditAccountCbuNumber.label`)}
                                                    idForm={FORM_ID}
                                                    name="creditCbu"
                                                    disabled={
                                                        !values.debitAccount || values.beneficiary.cbu || isSubmitting
                                                    }
                                                    type="text"
                                                    hidePlaceholder
                                                    onlyNumbers
                                                    handleOnChange={(e) => {
                                                        onChangeDebounce(e.target.value);
                                                        dispatch(
                                                            transferActions.setOtherTransferData({
                                                                ...values,
                                                                creditCbu: e.target.value,
                                                            }),
                                                        );
                                                    }}
                                                />
                                            </div>
                                            <Beneficiaries
                                                isDesktop={isDesktop}
                                                values={values}
                                                setValues={setFieldValue}
                                                setText={setText}
                                                onChange={(e) => handleBeneficiary(e, values)}
                                            />
                                            <GridTable.Data
                                                className="required"
                                                columnStart={2}
                                                columnWidth={isDesktop ? 5 : 10}>
                                                <Field
                                                    component={AmountField}
                                                    labelText={i18n.get(`${FORM_ID}.amount.label`)}
                                                    name="amountField"
                                                    disableNumber={!values.debitAccount}
                                                    data={{ options: currencies }}
                                                    clearable={false}
                                                    integerLength={10}
                                                    decimalLength={2}
                                                    onBlurChange={(amount) =>
                                                        handleOnChangeAmount(amount, values, setFieldValue)
                                                    }
                                                    withValueCap
                                                    disableSelect
                                                    fixedDecimalScale
                                                    hideCurrency
                                                    prefix={prefixValue}
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data
                                                className="required"
                                                columnStart={isDesktop ? 7 : 2}
                                                columnWidth={isDesktop ? 5 : 10}>
                                                <Field
                                                    component={Selector}
                                                    label={i18n.get(`${FORM_ID}.concept.label`)}
                                                    name="reasonCode"
                                                    options={conceptOptions}
                                                    placeholder={i18n.get(`${FORM_ID}.concept.placeholder`)}
                                                    onChange={(e) => handleConcept(e, values)}
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data columnStart={2} columnWidth={10}>
                                                <Field
                                                    component={TextField}
                                                    labelnoI18n={i18n.get(`${FORM_ID}.reference.label`)}
                                                    name="reference"
                                                    hidePlaceholder
                                                />
                                            </GridTable.Data>
                                        </GridTable.Container>
                                    </GridTable>
                                    <div>
                                        <I18n
                                            id="mep.transfer.message"
                                            defaultValue="Para realizar una transferencia inmediata de fondos,"
                                        />
                                        <Button
                                            onClick={handleClick}
                                            label="mep.click.label"
                                            defaultLabelText="hacé clic acá"
                                            bsStyle="text"
                                            block={false}
                                        />
                                    </div>
                                    <MepDisclaimer />

                                    <Row className="form-actions">
                                        <Col xs={12} md={4} mdOffset={4}>
                                            <Button
                                                type="submit"
                                                className="btn-primary"
                                                label="global.continue"
                                                loading={isSubmitting}
                                                disabled={disableSubmit || !allowedMep}
                                            />
                                        </Col>
                                    </Row>
                                </Form>
                            );
                        }}
                    </Formik>
                </PageLoading>
            </MainContainer>
        </>
    );
};

MepTransfersPage.propTypes = {
    isDesktop: bool.isRequired,
};

export default MepTransfersPage;
